import React, { useState, useEffect } from 'react';
import { apiGet, apiPost } from '../auth';
import { useLanguage } from '../contexts/LanguageContext';
import { X, Search, MapPin, UserPlus, Star, Loader2 } from 'lucide-react';

interface Surveyor {
  id: string;
  full_name: string;
  company: string;
  location: string;
}

interface AssignSurveyorModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  onAssignSuccess: () => void;
}

export const AssignSurveyorModal: React.FC<AssignSurveyorModalProps> = ({ isOpen, onClose, reportId, onAssignSuccess }) => {
  const [surveyors, setSurveyors] = useState<Surveyor[]>([]);
  const [loading, setLoading] = useState(false);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const { t } = useLanguage();

  useEffect(() => {
    if (isOpen) {
      fetchSurveyors();
    }
  }, [isOpen]);

  const fetchSurveyors = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet<Surveyor[]>('api/surveyor/list');
      setSurveyors(data);
    } catch (err: any) {
      setError(err.message || t('surveys.modal.list_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (surveyorId: string) => {
    setAssigningId(surveyorId);
    setError(null);
    try {
      await apiPost('api/surveyor/assign', {
        report_id: reportId,
        surveyor_id: surveyorId,
        notes: ''
      });

      onAssignSuccess();
    } catch (err: any) {
      setError(err.message || t('surveys.modal.assign_failed'));
    } finally {
      setAssigningId(null);
    }
  };

  if (!isOpen) return null;

  const filteredSurveyors = surveyors.filter(s =>
    s.location?.toLowerCase().includes(search.toLowerCase()) ||
    s.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    s.company?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50/50">
          <h3 className="text-lg font-semibold text-slate-800">{t('surveys.modal.title')}</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-slate-200 text-slate-400 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 border-b border-slate-100">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder={t('surveys.modal.search_placeholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
            />
          </div>
        </div>

        <div className="p-4 flex-1 overflow-y-auto">
          {error && (
            <div className="mb-4 bg-red-50 text-red-600 p-3 rounded text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin mb-2" />
              <p className="text-sm">{t('surveys.modal.loading')}</p>
            </div>
          ) : filteredSurveyors.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <p>{t('surveys.modal.not_found')}</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {filteredSurveyors.map(s => (
                <div key={s.id} className="border border-slate-100 p-4 rounded-lg flex justify-between items-center hover:bg-slate-50 transition-colors">
                  <div>
                    <h3 className="font-bold text-slate-900">{s.full_name}</h3>
                    <p className="text-sm text-slate-600">{s.company}</p>
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>{s.location || t('surveys.modal.unknown_location')}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleAssign(s.id)}
                    disabled={assigningId !== null}
                    className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white px-4 py-2 rounded-md text-xs font-medium transition-colors shadow-sm"
                  >
                    {assigningId === s.id ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('surveys.modal.assigning')}</>
                    ) : (
                      <><UserPlus className="w-3.5 h-3.5" /> {t('surveys.modal.assign_button')}</>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
